import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { linkedinApi } from '@/lib/linkedin-api';

interface LinkedInPublishCardProps {
  jobId: string;
  jobStatus: string;
  linkedinStatus?: string;
}

export function LinkedInPublishCard({ jobId, jobStatus, linkedinStatus: initialStatus }: LinkedInPublishCardProps) {
  const [isConnected, setIsConnected] = useState(false);
  const [publishStatus, setPublishStatus] = useState(initialStatus || 'not_published');
  const [isPublishing, setIsPublishing] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(true);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const status = await linkedinApi.getStatus();
      setIsConnected(status.status === 'connected');
    } catch {
      setIsConnected(false);
    } finally {
      setIsCheckingConnection(false);
    }
  };

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      await linkedinApi.publishJob(jobId);
      setPublishStatus('published');
      toast.success('Vaga publicada no LinkedIn');
    } catch (error: any) {
      setPublishStatus('error');
      toast.error(error.message || 'Erro ao publicar no LinkedIn');
    } finally {
      setIsPublishing(false);
    }
  };

  const canPublish = isConnected && jobStatus === 'open' && publishStatus !== 'published';

  const getStatusBadge = () => {
    switch (publishStatus) {
      case 'published':
        return <Badge variant="success">Publicada</Badge>;
      case 'error':
        return <Badge variant="destructive">Erro</Badge>;
      default:
        return <Badge variant="secondary">Não publicada</Badge>;
    }
  };

  if (isCheckingConnection) return null;

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">LinkedIn</CardTitle>
          {getStatusBadge()}
        </div>
      </CardHeader>
      <CardContent>
        {!isConnected ? (
          <p className="text-sm text-muted-foreground">
            Conecte sua conta LinkedIn em <a href="/integrations" className="text-primary underline">Integrações</a> para publicar vagas.
          </p>
        ) : publishStatus === 'published' ? (
          <p className="text-sm text-muted-foreground">Vaga publicada no LinkedIn.</p>
        ) : (
          <div className="space-y-2">
            {jobStatus !== 'open' && (
              <p className="text-sm text-muted-foreground">A vaga precisa estar aberta para publicar no LinkedIn.</p>
            )}
            <Button size="sm" onClick={handlePublish} disabled={!canPublish || isPublishing}>
              {isPublishing ? 'Publicando...' : 'Publicar no LinkedIn'}
            </Button>
            {publishStatus === 'error' && (
              <p className="text-sm text-destructive">Falha na publicação. Tente novamente.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
